// src/utils/sharingHelpers.ts
// Helper functions for share status, calculations, and display

import { SharedSession, ShareStatus } from '../components/types';

/**
 * Calculate remaining views for a share
 */
export const calculateViewsRemaining = (accessCount: number, maxViews: number): number => {
  return Math.max(0, maxViews - accessCount);
};

/**
 * Calculate remaining days until expiry
 */
export const calculateDaysRemaining = (expiresAt: Date | any): number => {
  const expiry = expiresAt instanceof Date ? expiresAt : expiresAt?.toDate?.() || new Date(expiresAt);
  const now = new Date();
  const diff = expiry.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

/**
 * Format time remaining in human-readable format
 */
export const formatTimeRemaining = (expiresAt: Date | any): string => {
  const days = calculateDaysRemaining(expiresAt);
  
  if (days === 0) return 'Expires today';
  if (days === 1) return '1 day left';
  if (days <= 7) return `${days} days left`;
  
  const expiry = expiresAt instanceof Date ? expiresAt : expiresAt?.toDate?.() || new Date(expiresAt);
  return `Expires ${expiry.toLocaleDateString()}`;
};

/**
 * Get share status based on views and expiry
 */
export const getShareStatus = (share: SharedSession | null): ShareStatus => {
  if (!share || !share.isActive) return 'none';
  
  const viewsRemaining = calculateViewsRemaining(share.accessCount, share.maxViews);
  const daysRemaining = calculateDaysRemaining(share.expiresAt);
  
  if (viewsRemaining === 0) return 'maxed';
  if (daysRemaining === 0) return 'expired';
  if (viewsRemaining <= 5 || daysRemaining <= 1) return 'near_limit';
  return 'active';
};

/**
 * Get display properties for share status
 * Returns emoji, text, color, and background color
 */
export const getShareStatusDisplay = (status: ShareStatus) => {
  switch (status) {
    case 'active':
      return {
        emoji: '✅',
        text: 'Active',
        color: '#059669',
        bgColor: '#d1fae5'
      };
    case 'near_limit':
      return {
        emoji: '⚠️',
        text: 'Near Limit',
        color: '#d97706',
        bgColor: '#fef3c7'
      };
    case 'expired':
      return {
        emoji: '⏰',
        text: 'Expired',
        color: '#dc2626',
        bgColor: '#fee2e2'
      };
    case 'maxed':
      return {
        emoji: '🚫',
        text: 'Max Views',
        color: '#dc2626',
        bgColor: '#fee2e2'
      };
    default:
      return {
        emoji: '',
        text: '',
        color: '#6b7280',
        bgColor: '#f3f4f6'
      };
  }
};

/**
 * Check if share is near any limit (views or expiry)
 */
export const isShareNearLimit = (share: SharedSession | null): boolean => {
  if (!share) return false;
  
  const viewsRemaining = calculateViewsRemaining(share.accessCount, share.maxViews);
  const daysRemaining = calculateDaysRemaining(share.expiresAt);
  
  return viewsRemaining <= 5 || daysRemaining <= 1;
};

/**
 * Check if share is expired or maxed out
 */
export const isShareExpiredOrMaxed = (share: SharedSession | null): boolean => {
  if (!share) return false;
  
  const status = getShareStatus(share);
  return status === 'expired' || status === 'maxed';
};

/**
 * Get warning message for share based on status
 */
export const getShareWarningMessage = (share: SharedSession | null): string | null => {
  if (!share) return null;
  
  const viewsRemaining = calculateViewsRemaining(share.accessCount, share.maxViews);
  const daysRemaining = calculateDaysRemaining(share.expiresAt);
  
  if (viewsRemaining === 0) {
    return 'This share has reached its maximum of 30 views.';
  }
  
  if (daysRemaining === 0) {
    return 'This share has expired (7 days maximum).';
  }
  
  if (viewsRemaining <= 5 && daysRemaining <= 1) {
    return `Only ${viewsRemaining} views and ${daysRemaining} day remaining!`;
  }
  
  if (viewsRemaining <= 5) {
    return `Only ${viewsRemaining} views remaining!`;
  }
  
  if (daysRemaining <= 1) {
    return `Only ${daysRemaining} day remaining!`;
  }
  
  return null;
};

/**
 * Calculate progress percentage for views
 */
export const calculateViewProgressPercentage = (accessCount: number, maxViews: number): number => {
  return Math.min(100, Math.round((accessCount / maxViews) * 100));
};

/**
 * Get progress bar color based on usage
 */
export const getProgressBarColor = (accessCount: number, maxViews: number): string => {
  const percentage = calculateViewProgressPercentage(accessCount, maxViews);
  
  if (percentage >= 83) return '#dc2626'; // Red (25+ views)
  if (percentage >= 50) return '#d97706'; // Orange (15+ views)
  return '#059669'; // Green
};